import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import { authService } from './auth.service'
import type { ApiResponse } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })

        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

// ============================================================================
// Type Definitions
// ============================================================================

export interface HealthCheckResult {
    overall_health: 'HEALTHY' | 'WARNING' | 'CRITICAL'
    last_check: string
    issues_found: number
    checks: {
        enrollments: {
            total: number
            orphaned: number
            missing_slug: number
            missing_product_id: number
            status: 'HEALTHY' | 'WARNING' | 'CRITICAL'
        }
        course_curriculum: {
            total_courses: number
            total_curriculums: number
            courses_without_curriculum: number
            orphaned_curriculums: number
            status: 'HEALTHY' | 'WARNING' | 'CRITICAL'
        }
        bundles: {
            total_bundles: number
            invalid_course_refs: number
            empty_bundles: number
            status: 'HEALTHY' | 'WARNING' | 'CRITICAL'
        }
        users: {
            total_users: number
            admin_users: number
            status: 'HEALTHY' | 'WARNING' | 'CRITICAL'
        }
    }
    recommendations: string[]
}

export interface QuickHealthResult {
    status: 'OK' | 'ERROR'
    database_connected: boolean
    total_enrollments: number
    total_users: number
    total_courses: number
}

export interface RepairResult {
    total_checked: number
    repaired: number
    failed: number
    skipped: number
    details: Array<{
        enrollment_id: string
        status: 'repaired' | 'failed' | 'skipped'
        message: string
        old_product_id?: string
        new_product_id?: string
    }>
}

export interface BundleRepairResult {
    total_bundles: number
    bundles_fixed: number
    total_invalid_refs_removed: number
    details: Array<{
        bundle_id: string
        bundle_title: string
        invalid_refs_removed: number
        status: 'fixed' | 'skipped' | 'failed'
    }>
}

export interface RepointRequest {
    new_product_id: string
    new_product_slug: string
    new_product_type: 'course' | 'bundle' | 'digital_product'
}

export interface BackupInfo {
    timestamp: string
    label?: string
    size_bytes: number
    collections: string[]
    document_count: number
}

export interface BackupCreateResult {
    success: boolean
    backup_path?: string
    timestamp?: string
    error?: string
}

// ============================================================================
// Service Class
// ============================================================================

class AdminHealthService {
    private validateAuth(): void {
        if (!authService.isAuthenticated()) {
            throw new Error('AUTHENTICATION_REQUIRED: No authentication token found. Please log in.')
        }
    }

    private handleAuthenticationFailure(error: any): void {
        const errorMessage = error.message || String(error)

        if (
            errorMessage.includes('Could not validate credentials') ||
            errorMessage.includes('Unauthorized') ||
            errorMessage.includes('Invalid token') ||
            errorMessage.includes('Token expired') ||
            errorMessage.includes('AUTHENTICATION_REQUIRED') ||
            error.response?.status === 401
        ) {
            console.error('Authentication failure detected. Logging out user...')

            setTimeout(() => {
                authService.logout()
            }, 1000)

            throw new Error('Your session has expired. Please log in again.')
        }
    }

    // ========================================================================
    // Health Check Operations
    // ========================================================================

    /**
     * Run full database health check
     */
    async runHealthCheck(): Promise<ApiResponse<HealthCheckResult>> {
        try {
            this.validateAuth()

            const response = await getApiInstance().get('/admin/health/check')

            const result = response.data?.data || response.data

            return {
                success: true,
                data: result
            }
        } catch (error: any) {
            console.error('Error running health check:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to run health check'
            }
        }
    }

    /**
     * Run quick health check
     */
    async runQuickCheck(): Promise<ApiResponse<QuickHealthResult>> {
        try {
            this.validateAuth()

            const response = await getApiInstance().get('/admin/health/quick')

            const result = response.data?.data || response.data

            return {
                success: true,
                data: result
            }
        } catch (error: any) {
            console.error('Error running quick check:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to run quick check'
            }
        }
    }

    // ========================================================================
    // Repair Operations
    // ========================================================================

    /**
     * Repair orphaned enrollments by slug (bulk)
     */
    async repairEnrollmentsBySlug(dryRun: boolean = true): Promise<ApiResponse<RepairResult>> {
        try {
            this.validateAuth()

            const response = await getApiInstance().post(
                `/admin/repair/enrollments/by-slug?dry_run=${dryRun}`
            )

            const result = response.data?.data || response.data

            return {
                success: true,
                data: result
            }
        } catch (error: any) {
            console.error('Error repairing enrollments:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to repair enrollments'
            }
        }
    }

    /**
     * Repoint a single enrollment to a new product
     */
    async repointEnrollment(
        enrollmentId: string,
        repointData: RepointRequest
    ): Promise<ApiResponse<any>> {
        try {
            this.validateAuth()

            const response = await getApiInstance().post(
                `/admin/repair/enrollment/${enrollmentId}/repoint`,
                repointData
            )

            const result = response.data?.data || response.data

            return {
                success: true,
                data: result
            }
        } catch (error: any) {
            console.error('Error repointing enrollment:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to repoint enrollment'
            }
        }
    }

    /**
     * Fix bundle course IDs
     */
    async fixBundleCourseIds(dryRun: boolean = true): Promise<ApiResponse<BundleRepairResult>> {
        try {
            this.validateAuth()

            const response = await getApiInstance().post(
                `/admin/repair/bundles/fix-course-ids?dry_run=${dryRun}`
            )

            const result = response.data?.data || response.data

            return {
                success: true,
                data: result
            }
        } catch (error: any) {
            console.error('Error fixing bundle course IDs:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to fix bundle course IDs'
            }
        }
    }

    // ========================================================================
    // Backup Operations
    // ========================================================================

    /**
     * Create a new backup
     */
    async createBackup(label?: string): Promise<ApiResponse<BackupCreateResult>> {
        try {
            this.validateAuth()

            const params = label ? `?label=${encodeURIComponent(label)}` : ''
            const response = await getApiInstance().post(`/admin/repair/backup/create${params}`)

            const result = response.data?.data || response.data

            return {
                success: true,
                data: result
            }
        } catch (error: any) {
            console.error('Error creating backup:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to create backup'
            }
        }
    }
}

// Export singleton instance
export const adminHealthService = new AdminHealthService()
export default adminHealthService
